import React from 'react';
import pattern_16 from '../assets/images/background/pattern-16.png';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import privacyOgImage from '../assets/images/main-slider/software-erp-development-hero.png';
const Privacy_policy = () => {
  const canonical = "https://www.swiftmazetech.com/privacy-policy/";
  const ogImage = `https://www.swiftmazetech.com${privacyOgImage}`;
  const description = "Read how SwiftMaze Tech and SwiftMaze CRM collect, use, store, share, and protect personal data, including Google Workspace and Gmail data.";
  return (
    <>
      <Helmet>
        <title>Privacy Policy | SwiftMaze Tech</title>
        <meta
          name="description"
          content={description}
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Privacy Policy | SwiftMaze Tech" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy | SwiftMaze Tech" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>
      <section className="page-title">
        <div
          className="pattern-layer-one"
          style={{ backgroundImage: `url(${pattern_16})` }}
        ></div>
        <div className="auto-container">
          <h2>Privacy Policy</h2>
          <ul className="page-breadcrumb">
            <li><Link to="/">Home</Link></li>
            <li>Privacy Policy</li>
          </ul>
        </div>
      </section>

      <section className="about-section">
        <div className="auto-container">
          <div className="sec-title">
            <div className="title">Privacy Policy</div>
            <h2>Our Commitment to Data Privacy</h2>
          </div>

          <div className="privacy-content">
            <p className="privacy-last-updated"><strong>Last updated:</strong> October 6, 2026</p>

            <h4>Data Collection and Usage</h4>
            <h5>Personal Information</h5>
            <ul>
              <li>We collect information you voluntarily provide when interacting with our website.</li>
              <li>This includes name, email address, and any other details shared through forms.</li>
              <li>Your IP address and browser information are collected for security purposes.</li>
            </ul>

            <h5>Comments and User Content</h5>
            <ul>
              <li>Comments and associated metadata are stored indefinitely.</li>
              <li>Profile pictures linked to comments are publicly visible.</li>
              <li>We use automated spam detection to protect our community.</li>
            </ul>

            <h5>Media and Uploads</h5>
            <ul>
              <li>Avoid uploading images containing embedded location data (EXIF GPS).</li>
              <li>Uploaded media may be publicly accessible.</li>
              <li>We recommend reviewing media files before uploading.</li>
            </ul>

            <div className="google-data-usage" id="google-workspace-gmail-data-usage">
              <h4>Google Workspace / Gmail Data Usage</h4>
              <p>
                SwiftMaze CRM uses Google OAuth only when a customer chooses to connect a Google Workspace or Gmail account inside SwiftMaze CRM.
              </p>

              <h5>Google user data we may access</h5>
              <p>When the customer grants permission, SwiftMaze CRM may access:</p>
              <ul>
                <li>The connected Gmail email address.</li>
                <li>Gmail message subject, sender, recipient, date, and message body.</li>
                <li>Gmail message IDs and history IDs required for synchronization and webhook processing.</li>
              </ul>

              <h5>How we use Google user data</h5>
              <p>SwiftMaze CRM uses this data only to provide customer-requested CRM functionality, including:</p>
              <ul>
                <li>Importing marketplace inquiry emails.</li>
                <li>Parsing inquiry and lead details.</li>
                <li>Creating and updating CRM leads.</li>
                <li>Enabling follow-up automation configured by the customer.</li>
                <li>Supporting synchronization, audit, and troubleshooting functionality.</li>
              </ul>

              <h5>Advertising and prohibited uses</h5>
              <p>
                SwiftMaze CRM does not use Google user data for advertising, retargeting, profiling, selling data, or unrelated marketing purposes.
              </p>

              <h5>Data sharing</h5>
              <p>
                SwiftMaze CRM does not sell or rent Google user data. We do not share Google user data with third parties except where necessary to provide functionality requested by the customer, comply with applicable law, or protect the security and integrity of the service.
              </p>

              <h5>Storage and retention</h5>
              <p>
                Google user data is stored securely and retained only for as long as reasonably necessary to provide CRM lead import, audit, troubleshooting, synchronization, and customer-configured automation functionality, or to meet applicable legal obligations.
              </p>

              <h5>Disconnecting Google access</h5>
              <p>
                Customers may disconnect Google access from SwiftMaze CRM at any time. After disconnection, SwiftMaze CRM stops new Gmail synchronization and webhook processing. Customers may also review or remove SwiftMaze CRM access from their{' '}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
                  Google Account permissions
                </a>.
              </p>

              <h5>Google API Limited Use compliance</h5>
              <p>
                SwiftMaze CRM&apos;s use and transfer of information received from Google APIs adheres to the{' '}
                <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
                  Google API Services User Data Policy
                </a>, including the Limited Use requirements.
              </p>
            </div>

            <h4>Cookie Policy</h4>
            <h5>Essential Cookies</h5>
            <ul>
              <li>Temporary cookies to verify browser compatibility.</li>
              <li>Login cookies valid for two days.</li>
              <li>Screen preference cookies valid for one year.</li>
              <li>“Remember Me” option extends login duration to two weeks.</li>
            </ul>

            <h5>Optional Cookies</h5>
            <ul>
              <li>Comment cookies store name and email for convenience.</li>
              <li>Article editing cookies store post IDs temporarily.</li>
              <li>All optional cookies can be controlled through browser settings.</li>
            </ul>

            <h4>Third-Party Content</h4>
            <h5>Embedded Content</h5>
            <ul>
              <li>Our site may display content from external websites.</li>
              <li>Third-party embeds follow their respective privacy policies.</li>
              <li>External content may track user interactions independently.</li>
            </ul>

            <h4>Data Protection Rights</h4>
            <h5>Your Rights</h5>
            <ul>
              <li>Request export of your personal data.</li>
              <li>Request deletion of your personal data.</li>
              <li>Edit your profile information at any time.</li>
              <li>Control your cookie preferences.</li>
            </ul>

            <h5>Data Retention</h5>
            <ul>
              <li>We retain necessary data for administrative purposes.</li>
              <li>Security and legal requirements may affect data retention.</li>
              <li>User account information is stored as long as the account is active.</li>
            </ul>

            <h4>Security Measures</h4>
            <h5>Data Protection</h5>
            <ul>
              <li>Implementation of security measures to protect user data.</li>
              <li>Regular security audits and updates.</li>
              <li>Restricted access to personal information.</li>
            </ul>

            <h5>Data Sharing</h5>
            <ul>
              <li>Limited sharing of personal data with third parties.</li>
              <li>IP addresses included in password reset emails.</li>
              <li>Automated spam detection services may process comments.</li>
            </ul>

            <h4>Contact Information</h4>
            <p>
              For privacy-related inquiries, Google data questions, or requests concerning access or deletion, please{' '}
              <Link to="/contact/">contact SwiftMaze Tech</Link>. This privacy policy is subject to updates. We recommend checking periodically for any changes.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Privacy_policy;
